import { DriveStep } from 'driver.js';
import { UserOnboardingStepId } from '../types';

const ONBOARDING_TOUR_STORAGE_KEY = 'smartpos.activeOnboardingTourStep';

const VALID_ONBOARDING_TOUR_STEPS: UserOnboardingStepId[] = [
  'outlets',
  'departments',
  'menu',
  'sections_tables',
  'section_printers',
  'takeaway_printers',
  'first_order',
  'users',
  'security',
];

export const ONBOARDING_TOUR_TARGETS = {
  accessControlsPage: 'security-access-controls-page',
  accessControlsSearch: 'security-access-controls-search',
  departmentsAddButton: 'departments-add-button',
  departmentsEmptyAddButton: 'departments-empty-add-button',
  departmentsPage: 'departments-page',
  departmentsTable: 'departments-table',
  menuAddItemButton: 'menu-add-item-button',
  menuCategoriesTab: 'menu-categories-tab',
  menuItemsGrid: 'menu-items-grid',
  menuPage: 'menu-page',
  outletsAddButton: 'outlets-add-button',
  outletsEmptyAddButton: 'outlets-empty-add-button',
  outletsGrid: 'outlets-grid',
  outletsPage: 'outlets-page',
  primaryOutletCard: 'primary-outlet-card',
  primaryOutletEditButton: 'primary-outlet-edit-button',
  printersAddButton: 'printers-add-button',
  printersPage: 'printers-page',
  printerConfigurationPage: 'printer-configuration-page',
  salesActionBill: 'sales-action-bill',
  salesActionDiscount: 'sales-action-discount',
  salesActionKOT: 'sales-action-kot',
  salesActionPanel: 'sales-action-panel',
  salesActionSettle: 'sales-action-settle',
  salesActionWaiter: 'sales-action-waiter',
  salesDiningPanel: 'sales-dining-panel',
  salesDiningTab: 'sales-dining-tab',
  salesMenuPanel: 'sales-menu-panel',
  salesPage: 'sales-page',
  salesPrimaryTable: 'sales-primary-table',
  sectionConfigurePrintersButton: 'section-configure-printers-button',
  sectionViewTablesButton: 'section-view-tables-button',
  sectionsAddButton: 'sections-add-button',
  sectionsPage: 'sections-page',
  sectionsTabSections: 'sections-tab-sections',
  sectionsTabTables: 'sections-tab-tables',
  sectionsTable: 'sections-table',
  securityAccessControlsLink: 'security-access-controls-link',
  takeawayPage: 'takeaway-printers-page',
  takeawaySaveButton: 'takeaway-printers-save-button',
  tablesAddButton: 'tables-add-button',
  tablesTable: 'tables-table',
  usersCreateButton: 'users-create-button',
  usersPage: 'users-page',
  usersSearch: 'users-search',
} as const;

export const onboardingTourSelector = (
  target: (typeof ONBOARDING_TOUR_TARGETS)[keyof typeof ONBOARDING_TOUR_TARGETS],
) => `[data-onboarding-tour="${target}"]`;

export const queueOnboardingTour = (stepId: UserOnboardingStepId) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.setItem(ONBOARDING_TOUR_STORAGE_KEY, stepId);
};

export const clearQueuedOnboardingTour = () => {
  if (typeof window === 'undefined') {
    return;
  }

  window.sessionStorage.removeItem(ONBOARDING_TOUR_STORAGE_KEY);
};

export const getQueuedOnboardingTour = (): UserOnboardingStepId | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const value = window.sessionStorage.getItem(ONBOARDING_TOUR_STORAGE_KEY);

  return VALID_ONBOARDING_TOUR_STEPS.includes(value as UserOnboardingStepId)
    ? (value as UserOnboardingStepId)
    : null;
};

export const resolveFirstTourElement = (
  selectors: string[],
): Element | null => {
  if (typeof document === 'undefined') {
    return null;
  }

  return (
    selectors
      .map((selector) => document.querySelector(selector))
      .find(Boolean) || null
  );
};

export const createTourElementResolver = (selectors: string[]) => () =>
  resolveFirstTourElement(selectors) as Element;

const resolveStepElement = (element: DriveStep['element']): Element | null => {
  if (!element) {
    return document.body;
  }

  if (typeof element === 'string') {
    return document.querySelector(element);
  }

  if (typeof element === 'function') {
    try {
      return element() || null;
    } catch {
      return null;
    }
  }

  return element;
};

export const areTourTargetsReady = (steps: DriveStep[]) =>
  steps.every((step) => resolveStepElement(step.element));
